import {
    ReactElement,
    RefObject,
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
} from 'react';
import { useApolloClient, useMutation, useQuery } from '@apollo/client/react';
import { useTranslation } from 'react-i18next';
import { mdiDownload } from '@mdi/js';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';

import {
    IOfferEventDetailFragment,
    IOfferEventSubscriptionStatus,
    IEmailTypeEnum,
    IFeature,
    DeleteOfferEventEnrollmentsDocument,
    UpdateOfferEventEnrollmentsDocument,
    IContentTypeValue,
    ExportOfferEventEnrollmentDocument,
    CurrentUserDocument,
    OfferEventGroupEnrollmentCountsDocument,
    SendEmailDocument,
} from 'graphql/types';
import { GroupConditionSelect } from 'common/forms/Field';
import { useApolloError } from 'common/hooks/useApolloError';
import { useOfferEventManageableEnrollmentList } from 'offer/hooks/useOfferEventManageableEnrollmentList';
import { SubscriptionTable } from 'offer/tables/SubscriptionsTable';
import { UserSelectDrawer } from 'user/components/UserSelector/UserSelectDrawer';
import { ConfirmDialog } from 'common/components/ConfirmDialog';
import { getExtraFieldsJSON } from 'common/utils/extraFields';
import { useSnackbar } from 'common/hooks/useSnackbar';
import { useFeature } from 'common/hooks/useFeature';
import { Button } from 'common/components/Button';
import { Chip, ChipGroup } from 'common/components/Chip';
import { Icon } from 'common/components/Icon';

import { AddUsersButton } from './AddUsersButton';
import { EUserSelectorMode } from './types';
import { UserSelectFooterAppend } from './UserSelectFooterAppend';

interface IProps {
    drawerRef?: RefObject<HTMLDivElement | null>;
    offerEvent: IOfferEventDetailFragment;
}

export const OfferEventManageableEnrollmentList = ({
    drawerRef,
    offerEvent,
}: IProps) => {
    const [translate] = useTranslation();
    const { showApolloError } = useApolloError();
    const [displaySnackbar] = useSnackbar();
    const { canUse: canExport } = useFeature(IFeature.Export);
    const [showExportDialog, setShowExportDialog] = useState(false);
    const [selectedGroupId, setSelectedGroupId] = useState('');
    const [exportReport, { loading: loadingExport }] = useMutation(
        ExportOfferEventEnrollmentDocument
    );
    const { data: currentUserData } = useQuery(CurrentUserDocument);
    const currentUserEmail = currentUserData?.currentUser?.email;

    const client = useApolloClient();
    const prevWindowTop = useRef(drawerRef?.current?.scrollTop || 0);
    const closeSelectorRef = useRef<((open: boolean) => void) | null>(null);
    const userSelectResetRef = useRef<(() => void) | null>(null);

    const {
        accessTrainings,
        accessExternalContents,
        accessCourses,
        accessScorms,
        groupEnrollmentCounts,
    } = offerEvent;

    // Flatten the groups from every group condition into a single unique list
    // so the user selector filters users based on the groupcondition groups.
    const groupConditionGroups = useMemo(() => {
        const groups =
            offerEvent.offer.groupConditions?.flatMap((condition) =>
                condition.groups.edges
                    .map((edge) => edge?.node)
                    .filter((node): node is NonNullable<typeof node> =>
                        Boolean(node)
                    )
            ) || [];

        return Array.from(
            new Map(groups.map((group) => [group.id, group])).values()
        );
    }, [offerEvent.offer.groupConditions]);

    const userSelectorQueryVariables = useMemo(
        () =>
            groupConditionGroups.length > 0
                ? { groupIds: groupConditionGroups.map((group) => group.id) }
                : undefined,
        [groupConditionGroups]
    );

    // Only include progress if there is any material having the ability to have progress
    const includeProgress =
        !!accessTrainings.edges.length ||
        !!accessExternalContents.edges.length ||
        !!accessCourses.edges.length ||
        !!accessScorms.edges.length;

    const {
        enrollments,
        loading: subscriptionsLoading,
        handleSearch,
        handleFilterSelect,
        searchQueryParam,
        selectionProps,
        filters,
        filtersLoading,
        selectedFilters,
        paginationSettings,
        setPage,
    } = useOfferEventManageableEnrollmentList(
        offerEvent.id,
        undefined, // Disabled users are not used in this component
        includeProgress
    );

    const [sendEmail, { loading: sendEmailLoading }] =
        useMutation(SendEmailDocument);
    const [updateEnrollment] = useMutation(UpdateOfferEventEnrollmentsDocument);
    const [deleteEnrollments, { loading: deleteEnrollmentsLoading }] =
        useMutation(DeleteOfferEventEnrollmentsDocument);

    useEffect(() => {
        if (subscriptionsLoading) return;

        // Makes sure the drawer is scrolled to the position it was before
        // after doing a mutation. The mutations will cause the table to refresh
        // and scroll the drawer to the top.
        if (drawerRef?.current && prevWindowTop.current) {
            drawerRef.current.scrollTo(0, prevWindowTop.current);

            prevWindowTop.current = 0;
        }
    }, [subscriptionsLoading]);

    const evictEnrollmentsFromCache = (skipForEvent?: boolean) => {
        client.cache.evict({
            fieldName: 'manageableOfferEventEnrollments',
        });

        if (!skipForEvent) {
            client.cache.evict({
                fieldName: 'manageableOfferEventEnrollmentsForEvent',
            });
        }

        client.cache.evict({
            fieldName: 'paginatedOfferEvents',
        });

        client.cache.gc();
    };

    const handleUpdateSubscription = async (
        enrollmentIds: string[],
        status: IOfferEventSubscriptionStatus,
        reason: string
    ) => {
        try {
            await updateEnrollment({
                variables: {
                    offerEventEnrollmentIds: enrollmentIds,
                    status,
                    reason,
                    includeProgress,
                },
            });
        } catch (error) {
            showApolloError(error);

            return;
        }

        evictEnrollmentsFromCache(true);

        return;
    };

    const handleDeleteEnrollments = async (enrollmentIds: string[]) => {
        // Save scroll position before doing the mutation
        prevWindowTop.current = drawerRef?.current?.scrollTop || 0;

        try {
            await deleteEnrollments({
                variables: {
                    offerEventEnrollmentIds: enrollmentIds,
                },
            });
        } catch (error) {
            showApolloError(error);

            return;
        }

        evictEnrollmentsFromCache();

        // Refetch the selector to make sure the correct users are selected
        client.cache.evict({ fieldName: 'paginatedUsers' });
        client.cache.gc();

        // Refresh groupEnrollmentCounts via a background query so the drawer
        // does not enter a loading state (unlike refetchQueries or cache eviction).
        client
            .query({
                query: OfferEventGroupEnrollmentCountsDocument,
                variables: { id: offerEvent.id },
                fetchPolicy: 'network-only',
            })
            .catch(() => {
                // Background refresh — failure is non-critical
            });

        return;
    };

    const handleEnrollmentsCreated = useCallback(() => {
        // Save scroll position before the re-render caused by cache eviction
        prevWindowTop.current = drawerRef?.current?.scrollTop || 0;
    }, [drawerRef]);

    const handleExport = async () => {
        try {
            const exportResults = await exportReport({
                variables: {
                    offerEventId: offerEvent.id,
                    q: searchQueryParam,
                    extraFields: getExtraFieldsJSON(selectedFilters || []),
                },
            });
            const fileUrl =
                exportResults.data?.exportOfferEventEnrollment?.fileUrl;

            if (fileUrl) {
                window.open(fileUrl, '_blank');
            } else {
                displaySnackbar(translate('exportDialog.exportSuccess'), {
                    variant: 'success',
                });
            }
        } catch (error) {
            showApolloError(error);

            return;
        }

        setShowExportDialog(false);
    };

    const renderGroupConditionFooter =
        groupEnrollmentCounts.length > 0
            ? (totalSelectedUsers: number): ReactElement => (
                  <GroupConditionSelect
                      groupEnrollmentCounts={groupEnrollmentCounts}
                      selectedUsersCount={totalSelectedUsers}
                      value={selectedGroupId}
                      onChange={setSelectedGroupId}
                  />
              )
            : undefined;

    const handleSendEmail = useCallback(
        async (message: string, userIds: string[]) => {
            let result;

            try {
                result = await sendEmail({
                    variables: {
                        emailType: IEmailTypeEnum.OfferEventInvite,
                        message,
                        userIds,
                        relatedType: IContentTypeValue.OfferEvent,
                        relatedId: offerEvent.id,
                    },
                });
            } catch (error) {
                showApolloError(error);

                return;
            }

            const { succeededIds = [], failedIds = [] } =
                result.data?.sendEmail?.response || {};

            userSelectResetRef.current?.();
            closeSelectorRef.current?.(false);

            if (succeededIds.length) {
                displaySnackbar(
                    translate('offerEventDetail.inviteEmail.emailSent', {
                        count: succeededIds.length,
                    }),
                    {
                        variant: 'success',
                    }
                );
            }

            if (failedIds.length) {
                displaySnackbar(
                    translate('offerEventDetail.inviteEmail.emailFailed', {
                        count: failedIds.length,
                    }),
                    { variant: 'error' }
                );
            }
        },
        [sendEmail, offerEvent.id, showApolloError, displaySnackbar, translate]
    );

    return (
        <AddUsersButton
            includeProgress={includeProgress}
            offerEvent={offerEvent}
            onEnrollmentsCreated={handleEnrollmentsCreated}
        >
            {({
                onAddUsers,
                loadingAddUsers,
                isUserSelectorOpen,
                userSelectorMode,
                onUserSelectorOpenChange,
            }) => {
                closeSelectorRef.current = onUserSelectorOpenChange;

                const loading =
                    subscriptionsLoading ||
                    loadingAddUsers ||
                    deleteEnrollmentsLoading;

                return (
                    <>
                        <SubscriptionTable
                            isEditable
                            filterBarAppend={
                                canExport && (
                                    <Button
                                        aria-label={translate('export')}
                                        disabled={!enrollments?.length}
                                        startIcon={
                                            <Icon
                                                path={mdiDownload}
                                                size="1.9rem"
                                            />
                                        }
                                        sx={{ flex: 1 }}
                                        variant="outlined"
                                        onClick={() =>
                                            setShowExportDialog(true)
                                        }
                                    />
                                )
                            }
                            loading={loading}
                            offerEvent={offerEvent}
                            paginationProps={{ paginationSettings, setPage }}
                            searchProps={{
                                handleSearch: handleSearch,
                                handleFilterSelect: handleFilterSelect,
                                searchQueryParam,
                                filters,
                                filtersLoading,
                                selectedFilters,
                            }}
                            selectionProps={selectionProps}
                            subscriptions={enrollments}
                            onCreateCertificates={evictEnrollmentsFromCache}
                            onDelete={handleDeleteEnrollments}
                            onStatusChange={handleUpdateSubscription}
                        />

                        <UserSelectDrawer
                            additionalDisabled={
                                userSelectorMode === EUserSelectorMode.Add &&
                                groupEnrollmentCounts.length > 0
                                    ? (totalSelectedUsers) => {
                                          if (!selectedGroupId) return false;
                                          const group =
                                              groupEnrollmentCounts.find(
                                                  ({ group: g }) =>
                                                      g.id === selectedGroupId
                                              );

                                          if (!group || !group.max) {
                                              return false;
                                          }

                                          return (
                                              totalSelectedUsers >
                                              group.max - group.count
                                          );
                                      }
                                    : undefined
                            }
                            footerAppend={
                                userSelectorMode !== EUserSelectorMode.Email
                                    ? renderGroupConditionFooter
                                    : undefined
                            }
                            infoMessage={
                                userSelectorMode === EUserSelectorMode.Email &&
                                groupConditionGroups.length > 0 && (
                                    <Box sx={{ mb: 3 }}>
                                        <Typography variant="body2">
                                            {translate(
                                                'offerEventDetail.inviteEmail.groupFilterNotice'
                                            )}
                                        </Typography>
                                        <ChipGroup sx={{ mt: 1 }}>
                                            {groupConditionGroups.map(
                                                (group) => (
                                                    <Chip
                                                        key={group.id}
                                                        label={group.name}
                                                    />
                                                )
                                            )}
                                        </ChipGroup>
                                    </Box>
                                )
                            }
                            loading={loading}
                            open={isUserSelectorOpen}
                            queryVariables={
                                userSelectorMode === EUserSelectorMode.Email
                                    ? userSelectorQueryVariables
                                    : undefined
                            }
                            renderFooterButton={
                                userSelectorMode === EUserSelectorMode.Email
                                    ? (selectedUserIds) => (
                                          <UserSelectFooterAppend
                                              loading={sendEmailLoading}
                                              selectedUserIds={selectedUserIds}
                                              onSendEmail={handleSendEmail}
                                          />
                                      )
                                    : undefined
                            }
                            resetRef={userSelectResetRef}
                            selectorQueryVariables={{
                                contentType: IContentTypeValue.OfferEvent,
                                objectId: offerEvent.id,
                            }}
                            title={
                                userSelectorMode === EUserSelectorMode.Email
                                    ? translate(
                                          'offerEventDetail.inviteEmail.drawerTitle'
                                      )
                                    : undefined
                            }
                            onAddUsers={async (userIds) => {
                                await onAddUsers(
                                    userIds,
                                    selectedGroupId || undefined
                                );
                                setSelectedGroupId('');
                            }}
                            onDrawerOpen={(open) => {
                                if (!open) setSelectedGroupId('');
                                onUserSelectorOpenChange(open);
                            }}
                        />

                        <ConfirmDialog
                            confirmText={translate('sendExport')}
                            loading={loadingExport}
                            open={showExportDialog}
                            title={translate(
                                `offerEventEnrollmentTable.exportDialog.title`
                            )}
                            onCancel={() => setShowExportDialog(false)}
                            onClose={() => setShowExportDialog(false)}
                            onConfirm={handleExport}
                        >
                            {translate(
                                `offerEventEnrollmentTable.exportDialog.text`,
                                {
                                    email: currentUserEmail,
                                }
                            )}
                        </ConfirmDialog>
                    </>
                );
            }}
        </AddUsersButton>
    );
};
